// Reads the shape of a result (which columns are numbers, dates or labels) and picks a way to show it:
// one big figure, a row of tiles, bars, a pivot grid, a line over time, or a table. Used by reports.
import type { Table } from "./sample";

export type Kind = "number" | "date" | "label" | "empty";
export type Column = {
  name: string;
  kind: Kind;
  distinct: number;
  decimals: number; // most digits after the point in any value (numbers only)
  additive: boolean; // summing it makes sense (counts, areas), unlike averages or percentages
};
export type View = "figure" | "tiles" | "bars" | "pivot" | "line" | "table";
export type Settings = { view?: View | "auto"; measure?: number; totals?: boolean; details?: boolean };
export type Plan = {
  columns: Column[];
  views: View[]; // the views this result can use, best first
  auto: View;
  view: View; // what to draw: the saved choice if it still fits, else auto
  measure: number; // the number column that bars, pivot and line draw
  dims: number[]; // label/date columns, in order (bars use the first, pivot the first two)
  totals: boolean;
  given?: (string | null)[]; // the result's own total row, shown as the totals line instead of our sums
};

export const VIEW_NAMES: Record<View, string> = {
  figure: "Figure", tiles: "Tiles", bars: "Bars", pivot: "Pivot", line: "Line", table: "Table",
};

const NULLISH = new Set(["", "NULL", "null", "<null>", "[null]", "(null)"]);
export const isNull = (v: string | null | undefined): v is null => v == null || NULLISH.has(v.trim());

// 1234, -12.5, 1,234,567.89, 1e6, 42% (but not 007: a leading zero means a code, not a number)
const NUM = /^[-+]?(?:\d{1,3}(?:,\d{3})+|\d+)?(?:\.\d+)?(?:e[-+]?\d+)?%?$/i;
export function toNumber(v: string | null): number | null {
  if (isNull(v)) return null;
  const s = v.trim();
  if (!/\d/.test(s) || !NUM.test(s) || /^[-+]?0\d/.test(s)) return null;
  const n = Number(s.replace(/,/g, "").replace(/%$/, ""));
  return Number.isFinite(n) ? n : null;
}

const DATE = /^\d{4}-\d{2}(?:-\d{2})?(?:[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?(?:Z|[+-]\d{2}(?::?\d{2})?)?$/;
const YEAR_NAME = /(^|[_\s])(year|yr)$|^year|ឆ្នាំ/i;
const ID_NAME = /(^|[_\s])(id|code|no|zip)$|^(id|code|gid|fid|oid|objectid)$/i;
const NOT_ADDITIVE = /avg|mean|average|median|pct|percent|ratio|rate|share|min|max|price|per_|_per|%|(^|[_\s])(lat|lon|lng|latitude|longitude|x|y|elev|elevation|score|age|rank|index)([_\s]|$)/i;
/** A pasted result that already ends in its own total row */
const TOTAL_LABEL = /^(grand )?total:?$|^សរុប(រួម)?$/i;

/** Takes a result's own total row off the end, so charts don't draw it as one more bar; it's shown as the totals line. */
export function splitTotal(t: Table): { table: Table; total?: (string | null)[] } {
  const last = t.rows.at(-1);
  if (t.rows.length < 3 || !last || !last.slice(0, 2).some((c) => TOTAL_LABEL.test((c ?? "").trim()))) return { table: t };
  return { table: { ...t, rows: t.rows.slice(0, -1) }, total: last };
}

/** Largest and smallest without spreading (a 10 MB paste has more rows than a call can take arguments). */
export function extent(vals: number[]): [number, number] {
  let lo = Infinity, hi = -Infinity;
  for (const v of vals) { if (v < lo) lo = v; if (v > hi) hi = v; }
  return [lo, hi];
}

/** Milliseconds for a date value (or a bare year in a year column), for placing it on a time axis. */
export function toTime(v: string | null, col?: Column): number | null {
  if (isNull(v)) return null;
  const s = v.trim();
  if (col && /^\d{4}$/.test(s)) return Date.UTC(Number(s), 0, 1);
  if (!DATE.test(s)) return null;
  const t = Date.parse(s.length === 7 ? `${s}-01` : s.replace(" ", "T"));
  return Number.isNaN(t) ? null : t;
}

function columnOf(t: Table, c: number): Column {
  const name = t.columns[c] ?? "";
  const vals = t.rows.map((r) => r[c] ?? null).filter((v): v is string => !isNull(v));
  const distinct = new Set(vals).size;
  const base = { name, distinct, decimals: 0, additive: false };
  if (!vals.length) return { ...base, kind: "empty" };
  if (vals.every((v) => DATE.test(v.trim()))) return { ...base, kind: "date" };
  const nums = vals.map(toNumber);
  if (nums.every((n) => n !== null)) {
    if (YEAR_NAME.test(name) && nums.every((n) => Number.isInteger(n) && n! >= 1800 && n! <= 2200)) return { ...base, kind: "date" };
    if (ID_NAME.test(name)) return { ...base, kind: "label" };
    const decimals = vals.reduce((d, v) => Math.max(d, (v.replace(/e.*$/i, "").split(".")[1] ?? "").replace(/%$/, "").length), 0);
    const additive = !NOT_ADDITIVE.test(name) && !vals.some((v) => v.trim().endsWith("%"));
    return { ...base, kind: "number", decimals, additive };
  }
  return { ...base, kind: "label" };
}

export const profile = (t: Table): Column[] => t.columns.map((_, c) => columnOf(t, c));

/** Each pair of the two dimensions appears at most once, and there are few enough of each to fit a grid. */
function pivotable(t: Table, a: number, b: number, cols: Column[]) {
  if (cols[a]!.distinct > 60 || cols[b]!.distinct > 14 || cols[a]!.distinct < 2 || cols[b]!.distinct < 2) return false;
  const seen = new Set<string>();
  for (const r of t.rows) {
    const k = `${r[a]}\u0000${r[b]}`;
    if (seen.has(k)) return false;
    seen.add(k);
  }
  return true;
}

export function plan(t: Table, s: Settings = {}, given?: (string | null)[]): Plan {
  const columns = profile(t);
  const n = t.rows.length;
  const measures = columns.flatMap((c, i) => (c.kind === "number" ? [i] : []));
  const labels = columns.flatMap((c, i) => (c.kind === "label" ? [i] : []));
  const dates = columns.flatMap((c, i) => (c.kind === "date" ? [i] : []));
  const dims = columns.flatMap((c, i) => (c.kind === "label" || c.kind === "date" ? [i] : []));

  // pivot: the dimension with fewer values goes across the top
  let pivotDims: number[] = [];
  if (dims.length >= 2 && measures.length && n >= 4) {
    const [a, b] = [dims[0]!, dims[1]!];
    const [rowDim, colDim] = columns[a]!.distinct >= columns[b]!.distinct ? [a, b] : [b, a];
    if (pivotable(t, rowDim, colDim, columns)) pivotDims = [rowDim, colDim];
  }

  const views: View[] = [];
  if (n === 1 && t.columns.length === 1) views.push("figure");
  if (n === 1 && t.columns.length >= 2 && t.columns.length <= 8) views.push("tiles");
  if (n === 1 && measures.length === 1 && t.columns.length > 1) views.push("figure");
  const line = dates.length > 0 && measures.length > 0 && n >= 3 && labels.length === 0;
  const pivot = pivotDims.length === 2;
  const bars = dims.length > 0 && measures.length > 0 && n >= 2 && n <= 60;
  // auto order: a time series reads best as a line; two dimensions as a grid; one short list as bars
  const firstRepeats = dims.length > 0 && columns[dims[0]!]!.distinct < n;
  if (line) views.push("line");
  if (pivot && firstRepeats) views.push("pivot");
  if (bars && !firstRepeats && n <= 25) views.push("bars");
  views.push("table");
  if (pivot && !views.includes("pivot")) views.push("pivot");
  if (bars && !views.includes("bars")) views.push("bars");

  const auto = views[0]!;
  const view = s.view && s.view !== "auto" && views.includes(s.view) ? s.view : auto;
  const measure = s.measure !== undefined && measures.includes(s.measure) ? s.measure : measures[0] ?? -1;
  const totals = s.totals ?? (given ? true : measures.some((m) => columns[m]!.additive) && n >= 2);
  return { columns, views, auto, view, measure, dims: view === "pivot" ? pivotDims : dims, totals, given };
}

/* ---------- formatting: the same everywhere, so an exported report reads the same on any machine ---------- */
/** Up to 2 decimals, or up to 4 for values under 1 so 0.0004 doesn't read as 0.00. */
export function formatNumber(n: number, decimals = 0) {
  const d = Math.min(decimals, n !== 0 && Math.abs(n) < 1 ? 4 : 2);
  return n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}
const compactFmt = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });
export const compact = (n: number) => (Math.abs(n) < 1000 ? formatNumber(n, Number.isInteger(n) ? 0 : 1) : compactFmt.format(n));

/** parcel_count -> Parcel count. Names with capitals, spaces or other scripts (Khmer) are left as they are. */
export const humanize = (name: string) =>
  /^[a-z0-9_]+$/.test(name) ? name.replace(/_+/g, " ").trim().replace(/^./, (c) => c.toUpperCase()) : name;

/** Round axis steps: 0, 250, 500, … up to just past max. */
export function niceTicks(min: number, max: number, count = 5): number[] {
  if (min === max) max = min + 1;
  const raw = (max - min) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw)!;
  const ticks: number[] = [];
  for (let v = Math.floor(min / step) * step; v < max + step * 0.999; v += step) ticks.push(Number(v.toPrecision(12)));
  return ticks;
}
